<template>
  <v-container fluid>

    <!-- Title -->
    <Title
        :title="fullName"
        :description="client ? 'Detalles del expediente' : ''"
    />

    <!-- Client details -->
    <v-row v-if="client">
      <v-col cols="12">
        <!-- Return button -->
        <ReturnButton class="me-4"/>
        <!-- Transfer button -->
        <v-menu v-if="isAdmin" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn @click="getAvailableOffices" color="primary" v-on="on">
              <v-icon left>mdi-office-building</v-icon>
              Transferir
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-if="isLoadingOffices">
              <v-list-item-content>
                <v-list-item-title>Cargando consultorios disponibles...</v-list-item-title>
                <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-else-if="offices.length === 0">
              <v-list-item-content>
                <v-list-item-title>No hay consultorios disponibles</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
                v-for="office in offices"
                :key="office.id"
                @click="handleClientOfficeChange(office)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ office.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="offices.length > 0" @click="handleClientOfficeChange(null)">
              <v-list-item-content>
                <v-list-item-title>
                  <em>Sin consultorio</em>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>

      <!-- Personal Information -->
      <v-col cols="12" md="6">
        <v-card
            :class="isEditingClientInformation ? 'edit-border-px' : ''"
            class="fill-height"
        >
          <v-card-title>
            <v-icon>mdi-account</v-icon>
            <span class="mx-2">{{
                isEditingClientInformation ? 'Editando información personal' : 'Información personal'
              }}</span>
            <v-btn
                :color="isEditingClientInformation ? 'primary' : ''"
                @click="handleEditInformationMode"
                icon
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-form ref="formClient" :readonly="!isEditingClientInformation">
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                      outlined
                      dense
                      label="Nombre *"
                      v-model="client.name"
                      :rules="[rules.required, rules.counter]"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                      outlined
                      dense
                      label="Teléfono *"
                      v-model="client.phone"
                      :rules="[rules.required, rules.phone]"
                      counter
                      maxlength="12"
                      @input="formatPhone"
                      prepend-inner-icon="mdi-phone-outline"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                      outlined
                      dense
                      label="Ocupación *"
                      v-model="client.occupation"
                      :rules="[rules.required, rules.counter]"
                      prepend-inner-icon="mdi-briefcase-outline"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="v-label theme--light pb-2 text-sm">
                    Fecha de nacimiento *
                  </div>
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field
                          v-model.number="selectedDay"
                          hide-spin-buttons
                          label="Día *"
                          type="number"
                          outlined
                          dense
                          required
                          :rules="dayRules"
                          @input="updateBirthdate"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                          v-model="selectedMonth"
                          :items="months"
                          item-text="text"
                          item-value="value"
                          label="Mes *"
                          outlined
                          dense
                          required
                          :rules="[rules.required]"
                          @input="updateBirthdate"
                          no-data-text="Mes no válido"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                          v-model="selectedYear"
                          :items="years"
                          label="Año *"
                          outlined
                          dense
                          required
                          :rules="[rules.required]"
                          @input="updateBirthdate"
                          no-data-text="Año no válido"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-list-item-content class="pt-0">
                        <v-list-item-title>
                          Edad:
                        </v-list-item-title>
                        <v-list-item-subtitle class="body-2">
                          {{ currentAgeDisplay || "Ingresa una fecha válida" }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                      v-model="client.province"
                      :items="provinces"
                      label="Estado *"
                      outlined
                      dense
                      required
                      :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                      v-model="client.city"
                      :items="cities"
                      label="Municipio *"
                      outlined
                      dense
                      required
                      :rules="[rules.required]"
                      :disabled="client.province === ''"
                      no-data-text="Selecciona un Estado primero"
                      v-if="client.province !== 'Otro / Extranjero' && client.province !== 'N/A'"
                  />
                  <v-text-field
                      v-model="client.city"
                      label="Ciudad *"
                      outlined
                      dense
                      required
                      :rules="[rules.required]"
                      v-else
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      outlined
                      dense
                      label="Colonia / Distrito"
                      v-model="client.district"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isAdmin">
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="client.heightCm"
                      label="Estatura (cm)"
                      outlined
                      dense
                      type="number"
                      :rules="[rules.required, rules.centimeter]"
                      prepend-inner-icon="mdi-ruler"
                      min="70"
                      max="250"
                      placeholder="170"
                      hide-spin-buttons
                      suffix="cm"
                      @keydown="preventNonNumeric($event)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="client.currentWeightKg"
                      label="Peso actual (kg)"
                      outlined
                      dense
                      type="number"
                      :rules="[rules.required, rules.kilogram]"
                      prepend-inner-icon="mdi-weight"
                      min="30"
                      max="400"
                      placeholder="80.0"
                      hide-spin-buttons
                      suffix="kg"
                      @keydown="preventNonNumeric($event)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="client.wristCircumferenceCm"
                      label="Circunferencia de la muñeca (cm)"
                      outlined
                      dense
                      type="number"
                      :rules="[rules.required, rules.wrist]"
                      prepend-inner-icon="mdi-tape-measure"
                      min="10"
                      max="30"
                      placeholder="15"
                      hide-spin-buttons
                      suffix="cm"
                      @keydown="preventNonNumeric($event)"
                  />
                </v-col>
                <v-col cols="12">
                  <v-radio-group
                      v-model="client.gender"
                      label="Sexo biológico *"
                      :rules="[rules.required]"
                      class="mt-0"
                      row
                  >
                    <v-radio
                        label="Masculino"
                        value="MALE"
                        on-icon="mdi-gender-male"
                    />
                    <v-radio
                        label="Femenino"
                        value="FEMALE"
                        on-icon="mdi-gender-female"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-expand-transition>
            <div v-if="isEditingClientInformation">
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <CancelButton
                    @click="handleEditInformationMode"
                    :isDisabled="isLoading"
                />
                <SaveButton
                    @click="updateClientInfo"
                    :isLoading="isLoading"
                    :isDisabled="!isValidForm"
                />
              </v-card-actions>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>

      <!-- Current quantity table -->
      <v-col cols="12" md="6">
        <v-card class="fill-height">
          <v-card-title>
            <v-icon>mdi-table</v-icon>
            <span class="mx-2">Tabla de cantidades actual</span>
            <v-spacer></v-spacer>
            <v-btn
                :loading="isLoading"
                color="green"
                class="white--text me-2"
                @click="openDialogNewQuantityTable"
            >
              <v-icon left>mdi-pencil</v-icon>
              Editar
            </v-btn>
            <v-btn
                v-if="currentQuantityTable"
                :loading="isLoading"
                color="primary"
                @click="printQuantityTable"
            >
              <v-icon left>mdi-printer</v-icon>
              Imprimir
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <QuantityTableComponent
                v-if="currentQuantityTable"
                :client-info="clientTableInfo"
                :quantity-table="currentQuantityTable"
                ref="printableQuantityTable"
            />
            <v-alert
                v-else
                outlined
                color="primary"
                type="info"
                border="left"
            >
              No se ha encontrado una tabla de cantidades actual para este cliente. Genera una para poder hacer un
              seguimiento.
            </v-alert>
          </v-card-text>
        </v-card>

        <DialogNewQuantityTable
            v-model="isNewQuantityTableDialogVisible"
            :hasEatenLunchOrDinner="currentQuantityTable.hasEatenLunch"
            :quantityTableProp="currentQuantityTable"
            :clientInfo="clientTableInfo"
            @printed="getClientDetails"
        />
      </v-col>

      <!-- Client File Information -->
      <v-col cols="12" md="6">
        <v-card class="fill-height">
          <v-card-title>
            <v-row>
              <v-col cols="8">
                <v-icon left>mdi-file-account</v-icon>
                <span class="text-break">Información del expediente ({{
                    client.officeName || "Sin consultorio"
                  }}) </span>
              </v-col>
              <v-col cols="4" class="d-flex justify-end align-center">
                <v-switch
                    v-if="isAdmin"
                    :color="client.status
                  ? 'success'
                  : 'secondary lighten-3'"
                    v-model="client.status"
                    :loading="isLoadingStatus"
                    :disabled="isLoadingStatus"
                    @click="handleStatusChange"
                    hide-details
                    class="mt-0 pt-0 ms-2"
                >
                  <template v-slot:label>
                    <StatusChip :status="client.status"/>
                  </template>
                </v-switch>
              </v-col>
            </v-row>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-row>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Fecha de registro</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ formatDate(client.createdAt) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Registrado por</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ client.createdBy }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Última actualización</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ formatDate(client.updatedAt) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Actualizado por</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{
                        client.updatedBy ? client.updatedBy : "N/A"
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Siguiente cita</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">
                      {{ client.nextAppointment ? formatDateAndTime(client.nextAppointment) : "N/A" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Nivel actual</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">
                      <v-chip
                          color="primary"
                          small
                          label
                      >
                        {{
                          client.currentLevel + client.currentClass
                        }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Peso inicial</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ client.initialWeightKg.toFixed(3) }} kg
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Peso actual</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">
                      {{ client.currentWeightKg.toFixed(3) }} kg
                      <small :class="lostWeight >= 0 ? 'green--text' : 'red--text'">
                        ({{ lostWeight >= 0 ? '-' : '+' }}{{ Math.abs(lostWeight).toFixed(3) }} kg)
                      </small>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Peso ideal máximo</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ client.idealWeightKg.toFixed(3) }} kg
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text">Peso deseado</v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">{{ client.desiredWeightKg.toFixed(3) }} kg
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text d-flex">
                      <span class="me-2">Tipo de cliente</span>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                              v-on="on"
                              icon
                              x-small
                              :loading="isLoadingType"
                              @click="handleClientTypeChange"
                          >
                            <v-icon color="primary">mdi-sync</v-icon>
                          </v-btn>
                        </template>
                        <span>Cambiar tipo de cliente</span>
                      </v-tooltip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">
                      <v-chip :color="typeToClassColor" small>{{ typeToText }}</v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="wrap-text d-flex">
                      <span class="me-2">Actividad física</span>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                              v-on="on"
                              icon
                              x-small
                              :loading="isLoadingActivityLevel"
                              @click="handleClientActivityLevelChange"
                          >
                            <v-icon color="primary">mdi-sync</v-icon>
                          </v-btn>
                        </template>
                        <span>Cambiar nivel de actividad física</span>
                      </v-tooltip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text">
                      <v-chip :color="activityToClassColor" small>{{ activityToText }}</v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>

          <DialogConfirmation
              ref="statusDialogConfirmation"
              @ok="changeStatus"
              @cancel="revertStatusChange"
              :confirmationTitle="originalStatus ?
                `¿Deshabilitar a ${client.name}?` :
                `¿Habilitar a ${client.name}?`"
              :message="'¿Estás seguro de que deseas ' + (originalStatus ? 'deshabilitar' : 'habilitar') + ' el expediente de ' +
                client.name + '?'"
          />

          <DialogConfirmation
              ref="typeDialogConfirmation"
              @ok="changeClientType"
              :confirmationTitle="`¿Cambiar tipo de cliente?`"
              :message="'¿Estás seguro de que deseas cambiar el tipo de cliente de ' +
                client.name +
                ' de ' + typeToText + ' a ' + (client.type === 'PRESENTIAL' ? 'En línea' : 'Presencial') + '?'"
          />

          <DialogConfirmation
              ref="activityLevelDialogConfirmation"
              @ok="changeClientActivityLevel"
              :confirmationTitle="`¿Cambiar actividad física?`"
              :message="'¿Estás seguro de que deseas cambiar el nivel de actividad física de ' +
                client.name +
                ' de ' + activityToText + ' a ' + (client.isSedentary ? 'Activo' : 'Sedentario') + '?'"
          />

          <DialogConfirmation
              ref="officeChangeDialogConfirmation"
              @ok="assignClientToOffice"
              :confirmationTitle="`¿Transferir expediente?`"
              :message="'¿Estás seguro de que deseas transferir el expediente de ' +
                client.name +
                (newSelectedOffice ? ' al consultorio ' + newSelectedOffice.name + '?' : ' a ningún consultorio?')"
          />
        </v-card>
      </v-col>

      <!-- Additional Client Information -->
      <v-col cols="12" md="6">
        <v-card class="fill-height">
          <!-- Card Title -->
          <v-card-title>
            <v-icon>mdi-file-document</v-icon>
            <span class="mx-2">
              Notas adicionales
            </span>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-textarea
                    outlined
                    dense
                    :loading="isLoadingNotes"
                    :disabled="isLoading"
                    label="Escribe aquí para editar..."
                    v-model="client.notes"
                    @input="onNotesInput"
                    rows="4"
                    counter
                    maxlength="255"
                    no-resize
                    hide-details
                />
              </v-col>

              <v-col cols="6" lg="6">
                <v-list-item class="px-0" v-if="!isEditingCatalogs">
                  <v-list-item-content class="pt-0">
                    <v-list-item-title class="wrap-text">
                      Padecimientos
                      <v-btn
                          icon
                          small
                          :loading="isLoadingCatalogs"
                          @click="handleCatalogEditing"
                          v-if="isAdmin"
                      >
                        <v-icon color="green">mdi-sync</v-icon>
                      </v-btn>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text" v-if="!client.ailments || client.ailments.length === 0">
                      N/A
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="text-break" v-for="ailment in client.ailments" :key="ailment.name">
                      —
                      <v-chip
                          color="green lighten-5"
                          small
                          label
                      >
                        {{ ailment.name }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <div v-else>
                  <v-combobox
                      v-model="client.ailments"
                      :items="ailmentsOptions"
                      item-text="name"
                      item-value="id"
                      label="Enfermedades o afecciones que padece"
                      outlined
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      persistent-hint
                      hint="Selecciona o agrega nuevas opciones. Pulsa Enter para agregar una opción nueva."
                      no-data-text="No se encontraron opciones"
                      prepend-inner-icon="mdi-book-heart"
                      @change="formatCatalog('ailments', $event)"
                      color="green"
                  />
                </div>
              </v-col>

              <v-col cols="6" lg="6">
                <v-list-item class="px-0" v-if="!isEditingCatalogs">
                  <v-list-item-content class="pt-0">
                    <v-list-item-title class="wrap-text">
                      Bebidas favoritas
                      <v-btn
                          icon
                          small
                          :loading="isLoadingCatalogs"
                          @click="handleCatalogEditing"
                          v-if="isAdmin"
                      >
                        <v-icon color="pink">mdi-sync</v-icon>
                      </v-btn>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text" v-if="!client.beverages || client.beverages.length === 0">
                      N/A
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="wrap-text" v-for="beverage in client.beverages" :key="beverage.name">
                      —
                      <v-chip
                          color="pink lighten-5"
                          small
                          label
                      >
                        {{ beverage.name }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <div v-else>
                  <v-combobox
                      v-model="client.beverages"
                      :items="beveragesOptions"
                      item-text="name"
                      item-value="id"
                      label="Bebidas que consume con frecuencia"
                      outlined
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      persistent-hint
                      hint="Selecciona o agrega nuevas opciones. Pulsa Enter para agregar una opción nueva."
                      no-data-text="No se encontraron opciones"
                      prepend-inner-icon="mdi-cup"
                      @change="formatCatalog('beverages', $event)"
                      color="pink"
                  />
                </div>
              </v-col>

              <v-col cols="6" lg="6">
                <v-list-item class="px-0" v-if="!isEditingCatalogs">
                  <v-list-item-content class="pt-0">
                    <v-list-item-title class="wrap-text">
                      Medicamentos
                      <v-btn
                          icon
                          small
                          :loading="isEditingCatalogs"
                          @click="handleCatalogEditing"
                          v-if="isAdmin"
                      >
                        <v-icon color="teal">mdi-sync</v-icon>
                      </v-btn>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text"
                                          v-if="!client.medications || client.medications.length === 0">
                      N/A
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="wrap-text" v-for="medication in client.medications"
                                          :key="medication.name">
                      —
                      <v-chip
                          color="teal lighten-5"
                          small
                          label
                      >
                        {{ medication.name }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <div v-else>
                  <v-combobox
                      v-model="client.medications"
                      :items="medicationsOptions"
                      item-text="name"
                      item-value="id"
                      label="Medicamentos que toma con frecuencia"
                      outlined
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      persistent-hint
                      hint="Selecciona o agrega nuevas opciones. Pulsa Enter para agregar una opción nueva."
                      no-data-text="No se encontraron opciones"
                      prepend-inner-icon="mdi-pill"
                      @change="formatCatalog('medications', $event)"
                      color="teal"
                  />
                </div>
              </v-col>

              <v-col cols="6" lg="6">
                <v-list-item class="px-0" v-if="!isEditingCatalogs">
                  <v-list-item-content class="pt-0">
                    <v-list-item-title class="wrap-text">
                      Antecedentes de dietas
                      <v-btn
                          icon
                          small
                          :loading="isEditingCatalogs"
                          @click="handleCatalogEditing"
                          v-if="isAdmin"
                      >
                        <v-icon color="deep-orange">mdi-sync</v-icon>
                      </v-btn>
                    </v-list-item-title>
                    <v-list-item-subtitle class="wrap-text" v-if="!client.diets || client.diets.length === 0">
                      N/A
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="wrap-text" v-for="diet in client.diets" :key="diet.name">
                      —
                      <v-chip
                          color="deep-orange lighten-5"
                          small
                          label
                      >
                        {{ diet.name }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <div v-else>
                  <v-combobox
                      v-model="client.diets"
                      :items="dietsOptions"
                      item-text="name"
                      item-value="id"
                      label="Dietas que ha seguido o sigue actualmente"
                      outlined
                      multiple
                      chips
                      deletable-chips
                      small-chips
                      persistent-hint
                      hint="Selecciona o agrega nuevas opciones. Pulsa Enter para agregar una opción nueva."
                      no-data-text="No se encontraron opciones"
                      prepend-inner-icon="mdi-food"
                      @change="formatCatalog('diets', $event)"
                      color="deep-orange"
                  />
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-expand-transition>
            <div v-if="isEditingCatalogs">
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <CancelButton
                    class="me-2"
                    @click="handleCatalogEditing"
                    :isLoading="isLoadingCatalogs"
                />
                <SaveButton
                    @click="saveCatalogs"
                    :isLoading="isLoadingCatalogs"
                />
              </v-card-actions>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <!-- Skeleton loader -->
    <OverlayLoading v-else/>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";

import {Client} from "@/services/clients/entities/Client";
import {OfficeName} from "@/services/offices/entities/OfficeName";
import {RequestUpdateClientInfo} from "@/services/clients/entities/RequestUpdateClientInfo";
import {RequestChangeClientStatus} from "@/services/clients/entities/RequestChangeClientStatus";
import {ClientTableInfo} from "@/services/clients/entities/ClientTableInfo";
import {QuantityTableInfo} from "@/services/quantityTables/entities/QuantityTableInfo";

import ClientService from "@/services/clients/ClientService";
import OfficeService from "@/services/offices/OfficeService";
import CatalogService from "@/services/catalogs/CatalogService";

import Title from "@/components/Title.vue";
import OverlayLoading from "@/components/OverlayLoading.vue";
import DialogConfirmation from "@/components/DialogConfirmation.vue";
import StatusChip from "@/components/StatusChip.vue";
import CancelButton from "@/components/buttons/CancelButton.vue";
import ReturnButton from "@/components/buttons/ReturnButton.vue";
import SaveButton from "@/components/buttons/SaveButton.vue";
import QuantityTableComponent from "@/components/QuantityTableComponent.vue";
import DialogNewQuantityTable from "@/views/clients/components/DialogNewQuantityTable.vue";

import throwErrorMessage from "@/utils/ErrorMessage";
import throwSuccessMessage from "@/utils/SuccessMessage";
import {printElement} from "@/utils/UniversalFunctions";
import {cities, provinces} from "@/utils/Locations";
import {CatalogElement} from "@/services/catalogs/entities/CatalogElement";

export default Vue.extend({
  name: "ClientDetails",
  components: {
    DialogNewQuantityTable,
    QuantityTableComponent,
    SaveButton,
    ReturnButton,
    CancelButton,
    StatusChip,
    DialogConfirmation,
    Title,
    OverlayLoading
  },
  data() {
    return {
      // Loading
      isLoading: false as boolean,
      isLoadingStatus: false as boolean,
      isLoadingOffices: false as boolean,
      isLoadingType: false as boolean,
      isLoadingActivityLevel: false as boolean,
      isLoadingNotes: false as boolean,
      isLoadingCatalogs: false as boolean,

      // Editing
      isEditingClientInformation: false as boolean,
      isEditingCatalogs: false as boolean,
      isNewQuantityTableDialogVisible: false as boolean,
      originalStatus: false as boolean,
      debounceTimer: null as ReturnType<typeof setTimeout> | null,

      // Client details
      client: null as Client | null,
      clientTableInfo: {} as ClientTableInfo,
      originalClient: null as Client | null,
      clientPublicId: '' as string,
      selectedDay: null as number | null,
      selectedMonth: null as number | null,
      selectedYear: null as number | null,

      // Catalogs
      beveragesOptions: [] as CatalogElement[],
      medicationsOptions: [] as CatalogElement[],
      ailmentsOptions: [] as CatalogElement[],
      dietsOptions: [] as CatalogElement[],

      // Locations
      provinces: provinces,
      cities: [] as string[],

      // Months
      months: [
        {text: 'Enero', value: 1},
        {text: 'Febrero', value: 2},
        {text: 'Marzo', value: 3},
        {text: 'Abril', value: 4},
        {text: 'Mayo', value: 5},
        {text: 'Junio', value: 6},
        {text: 'Julio', value: 7},
        {text: 'Agosto', value: 8},
        {text: 'Septiembre', value: 9},
        {text: 'Octubre', value: 10},
        {text: 'Noviembre', value: 11},
        {text: 'Diciembre', value: 12},
      ],

      // Quantity table
      currentQuantityTable: {} as QuantityTableInfo,

      // Offices
      offices: [] as OfficeName[],
      newSelectedOffice: null as OfficeName | null,

      // Validation
      rules: {
        required: (value: string) => !!value || 'Campo requerido',
        counter: (value: string) => (value || '').length <= 50 || 'Máximo 50 caracteres',
        usernameCounter: (value: string) => (value || '').length <= 20 || 'Máximo 20 caracteres',
        phone: (value: string) => {
          const pattern = /^\d{3}-\d{3}-\d{4}$/;
          return pattern.test(value) || 'Teléfono inválido';
        },
        centimeter: (value: string) => {
          const isInteger = /^\d+$/.test(value);
          if (!isInteger) return 'Solo se permiten números enteros';
          const numericValue = parseInt(value, 10);
          return (numericValue >= 70 && numericValue <= 250) || 'Ingresa un valor entre 70 y 250';
        },
        kilogram: (value: string) => {
          const isThreeDecimals = /^\d+(\.\d{1,3})?$/.test(value);
          if (!isThreeDecimals) return 'Solo se permiten números con hasta tres decimales';
          const numericValue = parseInt(value, 10);
          return (numericValue >= 30 && numericValue <= 400) || 'Ingresa un valor entre 30 y 400';
        },
        wrist: (value: string) => {
          const isOneDecimal = /^\d+(\.\d)?$/.test(value);
          if (!isOneDecimal) return 'Solo se permiten números con un decimal';
          const numericValue = parseFloat(value);
          return (numericValue >= 10 && numericValue <= 30) || 'Ingresa un valor entre 10 y 30';
        },
      },
    }
  },

  watch: {
    'client.province': function (val: string) {
      this.cities = cities[val] || [];
    },
    maxDaysInMonth(newMax: number) {
      if (this.selectedDay && this.selectedDay > newMax) {
        this.selectedDay = null;
        this.updateBirthdate();
      }
    },
  },

  computed: {
    sessionUser() {
      return this.$store.getters["auth/getUser"]
    },
    isAdmin(): boolean {
      return this.sessionUser.role !== 'DIETICIAN';
    },
    lostWeight(): number {
      if (!this.client?.initialWeightKg || !this.client.currentWeightKg) return 0;
      return this.client.initialWeightKg - this.client.currentWeightKg;
    },
    typeToText(): string {
      switch (this.client?.type ?? '') {
        case 'PRESENTIAL':
          return 'Presencial';
        case 'REMOTE':
          return 'En línea';
        default:
          return 'Desconocido';
      }
    },
    typeToClassColor(): string {
      switch (this.client?.type ?? '') {
        case 'PRESENTIAL':
          return 'blue lighten-5 blue--text';
        case 'REMOTE':
          return 'purple lighten-5 purple--text';
        default:
          return 'grey--text';
      }
    },
    activityToText(): string {
      return this.client?.isSedentary ? 'Sedentario' : 'Activo';
    },
    activityToClassColor(): string {
      return this.client?.isSedentary ? 'cyan lighten-5 cyan--text' : 'teal lighten-5 teal--text';
    },
    isValidForm(): boolean {
      if (!this.client) return false;
      return (
          !!this.client.name &&
          !!this.client.occupation &&
          !!this.client.phone &&
          !!this.client.birthdate &&
          !!this.client.province &&
          !!this.client.city &&
          !!this.client.district &&
          !!this.client.gender &&
          !!this.client.heightCm &&
          !!this.client.currentWeightKg &&
          !!this.client.wristCircumferenceCm
      )
    },
    fullName(): string {
      if (!this.client) return '';
      return `${this.client.name} - N${this.client.currentLevel + this.client.currentClass}`;
    },
    formattedBirthdate(): string | null {
      if (!this.client) return null;
      const [year, month, day] = this.client.birthdate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat('es-MX', options).format(date);
    },
    years(): number[] {
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - 100;
      const maxYear = currentYear - 14;
      const years = [];
      for (let year = maxYear; year >= minYear; year--) {
        years.push(year);
      }
      return years;
    },
    maxDaysInMonth(): number {
      if (!this.selectedMonth || !this.selectedYear) return 31;
      return new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    },
    dayRules(): any[] {
      return [
        this.rules.required,
        (value: number) => value >= 1 || 'El día debe ser al menos 1',
        (value: number) => value <= this.maxDaysInMonth || `El mes seleccionado tiene máximo ${this.maxDaysInMonth} días`
      ]
    },
    currentAgeDisplay(): string | null {
      if (!this.selectedYear || !this.selectedMonth || !this.selectedDay) return null;

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const currentDay = today.getDate();

      let age = currentYear - this.selectedYear;
      const monthDiff = currentMonth - this.selectedMonth;
      const dayDiff = currentDay - this.selectedDay;

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      return `${age} años`;
    }
  },

  methods: {
    async getClientDetails() {
      this.isLoading = true;
      try {
        const response = await ClientService.getClientDetails(this.clientPublicId);
        this.client = response.client;
        this.currentQuantityTable = response.quantityTable;
        if (this.client) {
          this.client.currentWeightKg = Number(this.client.currentWeightKg.toFixed(3));
          const birthdate = this.client.birthdate;
          const [birthYear, birthMonth, birthDay] = birthdate.split('-').map(Number);
          this.selectedYear = birthYear;
          this.selectedMonth = birthMonth;
          this.selectedDay = birthDay;
          this.clientTableInfo = {
            idClient: this.client.idClient,
            publicId: this.client.publicId,
            name: this.client.name,
            initialWeightKg: this.client.initialWeightKg,
            idealWeightKg: this.client.idealWeightKg,
            currentWeightKg: this.client.currentWeightKg,
            lostWeightKg: this.lostWeight,
            BMI: this.client.BMI,
            currentBreakfastPercentage: this.client.currentBreakfastPercentage,
            currentLunchPercentage: this.client.currentLunchPercentage,
            currentDinnerPercentage: this.client.currentDinnerPercentage,
          }
        }
      } catch {
        await this.$router.push({name: 'NotFound'});
      } finally {
        this.isLoading = false;
      }
    },
    async updateClientInfo() {
      if (!this.client) return;
      if (!this.isValidForm) return;
      const form = this.$refs.formClient as any;
      if (!form.validate()) {
        throwErrorMessage("Asegúrate de llenar todos los campos correctamente");
        return;
      }
      this.isLoading = true;
      try {
        const body: RequestUpdateClientInfo = {
          idClient: this.client.idClient,
          name: this.client.name,
          occupation: this.client.occupation,
          phone: this.client.phone,
          birthdate: this.client.birthdate,
          province: this.client.province,
          city: this.client.city,
          district: this.client.district,
          heightCm: this.client.heightCm,
          currentWeightKg: this.client.currentWeightKg,
          wristCircumferenceCm: this.client.wristCircumferenceCm,
          gender: this.client.gender,
        }
        await ClientService.updateClientInfo(body);
        throwSuccessMessage(`La información de ${this.client.name} ha sido actualizada correctamente`);
        this.originalClient = {...this.client};
      } finally {
        this.isLoading = false;
        this.isEditingClientInformation = false;
      }
    },
    async updateClientNotes() {
      if (!this.client) return;
      if (!this.isValidForm) return;
      this.isLoadingNotes = true;
      try {
        const body = {
          idClient: this.client.idClient,
          notes: this.client.notes,
        }
        await ClientService.updateClientNotes(body);
        throwSuccessMessage(`Las notas de ${this.client.name} han sido actualizadas correctamente`);
      } finally {
        this.isLoadingNotes = false;
      }
    },
    async getAvailableOffices() {
      if (this.offices.length > 0) return;
      this.isLoadingOffices = true;
      try {
        this.offices = await OfficeService.getAvailableOffices();
      } finally {
        this.isLoadingOffices = false;
      }
    },
    async assignClientToOffice() {
      if (!this.client) return;

      this.isLoading = true;
      try {
        const body = {
          idOffice: this.newSelectedOffice?.id || null,
          idClient: this.client.idClient
        }
        await ClientService.assignClientToOffice(body);
        this.client.officeName = this.newSelectedOffice?.name || null;
        throwSuccessMessage(`El expediente de ${this.client.name} ha sido transferido correctamente`);
      } finally {
        this.isLoading = false;
      }
    },
    async changeClientType() {
      if (!this.client) return;
      this.isLoadingType = true;
      try {
        const body = {
          idClient: this.client.idClient,
          newType: this.client.type === 'PRESENTIAL' ? 'REMOTE' : 'PRESENTIAL'
        }
        await ClientService.changeClientType(body);
        this.client.type = body.newType;

        throwSuccessMessage(`${this.client.name} ha sido cambiado a ` +
            (this.client.type === 'PRESENTIAL' ? 'Presencial' : 'En línea') + ' correctamente');
      } finally {
        this.isLoadingType = false;
      }
    },
    async changeStatus() {
      if (!this.client) return;
      this.isLoadingStatus = true;
      try {
        const body: RequestChangeClientStatus = {
          idClient: this.client.idClient,
          status: this.client.status
        }
        await ClientService.changeClientStatus(body);

        throwSuccessMessage(`El expediente de ${this.client.name} ha sido ` +
            (this.client.status ? 'habilitado' : 'deshabilitado') + ' correctamente');
      } finally {
        this.isLoadingStatus = false;
      }
    },
    async changeClientActivityLevel() {
      if (!this.client) return;
      this.isLoadingActivityLevel = true;
      try {
        await ClientService.changeClientActivityLevel(this.clientPublicId);
        this.client.isSedentary = !this.client.isSedentary;

        throwSuccessMessage(`El nivel de actividad física de ${this.client.name} ha sido cambiado a ${this.activityToText} correctamente`);
      } finally {
        this.isLoadingActivityLevel = false;
      }
    },
    async getCatalogs() {
      this.isLoadingCatalogs = true;
      try {
        const response = await CatalogService.getCatalogsList();
        this.beveragesOptions = response.beverages;
        this.medicationsOptions = response.medications;
        this.ailmentsOptions = response.ailments;
        this.dietsOptions = response.diets;
      } finally {
        this.isLoadingCatalogs = false;
      }
    },
    async saveCatalogs() {
      if (!this.client) return;
      this.isLoadingCatalogs = true;
      try {
        const body = {
          clientId: this.client.idClient,
          ailments: this.client.ailments,
          beverages: this.client.beverages,
          diets: this.client.diets,
          medications: this.client.medications,
        }
        await ClientService.updateClientCatalogs(body);
        throwSuccessMessage(`Los catálogos de ${this.client.name} han sido actualizadas correctamente`);
      } finally {
        this.isLoadingCatalogs = false;
        this.handleCatalogEditing();
      }
    },
    handleCatalogEditing() {
      if (!this.client) return;
      this.isEditingCatalogs = !this.isEditingCatalogs;
      const anyCatalogIsEmpty =
          this.ailmentsOptions.length === 0 ||
          this.beveragesOptions.length === 0 ||
          this.dietsOptions.length === 0 ||
          this.medicationsOptions.length === 0;
      if (anyCatalogIsEmpty) {
        this.getCatalogs();
      }
    },
    handleClientOfficeChange(office: OfficeName) {
      this.newSelectedOffice = office;
      (this.$refs.officeChangeDialogConfirmation as any).dialog = true;
    },
    handleClientTypeChange(): void {
      (this.$refs.typeDialogConfirmation as any).dialog = true;
    },
    handleStatusChange() {
      this.originalStatus = !this.client?.status;
      (this.$refs.statusDialogConfirmation as any).dialog = true;
    },
    handleEditInformationMode() {
      if (!this.client) return;
      if (this.isEditingClientInformation) {
        // Cancel editing, revert changes
        if (this.originalClient) {
          this.client = {...this.originalClient};
        }
      } else {
        // Start editing, save original state
        this.originalClient = {...this.client};
      }
      this.isEditingClientInformation = !this.isEditingClientInformation;
    },
    handleClientActivityLevelChange(): void {
      (this.$refs.activityLevelDialogConfirmation as any).dialog = true;
    },
    revertStatusChange() {
      if (!this.client) return;
      this.client.status = this.originalStatus;
      this.isLoadingStatus = false;
    },
    preventNonNumeric(event: KeyboardEvent) {
      if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
      }
    },
    formatPhone(value: string) {
      if (!value) return;
      if (!this.client) return;
      let digits = value.replace(/\D/g, '');

      if (digits.length > 10) {
        digits = digits.substring(0, 10);
      }

      if (digits.length > 6) {
        this.client.phone = digits.replace(/(\d{3})(\d{3})(\d{0,4})/, '$1-$2-$3');
      } else if (digits.length > 3) {
        this.client.phone = digits.replace(/(\d{3})(\d{0,3})/, '$1-$2');
      } else {
        this.client.phone = digits;
      }
    },
    formatDate(date: string) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    formatDateAndTime(date: string) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString('es-MX', {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    },
    printQuantityTable(): void {
      const quantityTableComponent = (this.$refs.printableQuantityTable as Vue).$el as HTMLElement;
      if (!quantityTableComponent) return;
      printElement(quantityTableComponent, this.$vuetify, this.currentQuantityTable, this.clientTableInfo);
    },
    onNotesInput() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.updateClientNotes();
      }, 1500);
    },
    formatCatalog(catalog: 'beverages' | 'medications' | 'ailments' | 'diets', values: any[]) {
      if (!this.client) return;
      this.client[catalog] = values.map(item => {
        if (typeof item === 'string' && item.trim() !== '') {
          return {id: '', name: item.trim()};
        }
        return item;
      }).filter(item => item);
    },
    updateBirthdate() {
      if (!this.client) return;
      if (this.selectedDay && this.selectedMonth && this.selectedYear && this.selectedDay <= this.maxDaysInMonth) {
        const monthString = this.selectedMonth.toString().padStart(2, '0');
        const dayString = this.selectedDay.toString().padStart(2, '0');
        this.client.birthdate = `${this.selectedYear}-${monthString}-${dayString}`;
      } else {
        this.client.birthdate = '';
      }
    },
    formatLocalToISO(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    openDialogNewQuantityTable() {
      this.isNewQuantityTableDialogVisible = true;
    },
  },

  mounted() {
    this.clientPublicId = this.$route.params.id;
    this.getClientDetails();
  }
})
</script>

<style scoped>
.edit-border-px {
  border: 1px solid #ff6600 !important;
  transition: border 0.5s !important;
}

.wrap-text {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

.text-sm {
  font-size: 0.875rem;
}
</style>